import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { Lock, AlertCircle, CheckCircle } from 'lucide-react';
import './Login.css';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null);

  useEffect(() => {
    // Check if we have a valid recovery session
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      // Check URL hash for recovery token (Supabase includes type=recovery)
      const hash = window.location.hash;
      const isRecovery = hash.includes('type=recovery') || hash.includes('type=signup');

      if (session || isRecovery) {
        setIsValidSession(true);
      } else {
        setIsValidSession(false);
      }
    };

    // Listen for auth state changes (recovery session)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsValidSession(true);
      }
    });

    checkSession();

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      setSuccess(true);

      // Sign out and redirect to login after a short delay
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Error updating password');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while checking session
  if (isValidSession === null) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <img src="/pilllogo.svg" alt="SiteOperations.io" style={{ height: 28, marginRight: 8 }} />
              SiteOperations.io
            </div>
            <p>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid or expired link
  if (!isValidSession) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <img src="/pilllogo.svg" alt="SiteOperations.io" style={{ height: 28, marginRight: 8 }} />
              SiteOperations.io
            </div>
            <p>Reset Password</p>
          </div>

          <div className="error-message" style={{ marginBottom: 16 }}>
            <AlertCircle size={16} />
            This password reset link is invalid or has expired.
          </div>

          <button
            onClick={() => navigate('/login')}
            className="login-button"
          >
            Back to Login
          </button>

          <div className="login-footer">
            <p style={{ color: 'var(--so-text-2)', fontSize: '0.875rem' }}>
              Need a new reset link? Go to login and click "Forgot password?"
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <img src="/pilllogo.svg" alt="SiteOperations.io" style={{ height: 28, marginRight: 8 }} />
              SiteOperations.io
            </div>
            <p>Password Updated</p>
          </div>

          <div className="success-message">
            <CheckCircle size={16} />
            Your password has been updated successfully. Redirecting to login...
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img src="/pilllogo.svg" alt="SiteOperations.io" style={{ height: 28, marginRight: 8 }} />
            SiteOperations.io
          </div>
          <p>Create a new password</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="password">
              <Lock size={16} />
              New Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              required
              minLength={6}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              <Lock size={16} />
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength={6}
            />
          </div>

          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>

        <div className="login-footer">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="link-button"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
